import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Cookie } from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "tv_cookie_consent_v1";

function loadGA() {
  // Fires Google Analytics only after consent
  if (typeof window === "undefined") return;
  if (window.gtag) return; // already loaded
  const scriptId = "ga-loader-script";
  if (document.getElementById(scriptId)) return;
  // Placeholder: real ID is inserted by index.html when Teresa provides GA ID
  // The gtag consent update signals to GA that analytics_storage is granted.
  if (window.gtag) window.gtag("consent", "update", { analytics_storage: "granted" });
}

function denyGA() {
  if (typeof window === "undefined") return;
  if (window.gtag) window.gtag("consent", "update", { analytics_storage: "denied" });
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Small delay for smooth entrance after page load
      const t = setTimeout(() => setVisible(true), 700);
      return () => clearTimeout(t);
    }
    if (stored === "accepted") loadGA();
    if (stored === "denied") denyGA();
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    loadGA();
    setVisible(false);
  };

  const deny = () => {
    localStorage.setItem(STORAGE_KEY, "denied");
    denyGA();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 md:left-8 md:right-auto md:max-w-[520px] z-[80] bg-[#FAF8F5] rounded-3xl shadow-2xl border border-[#3D2140]/10 p-6 md:p-7"
          data-testid="cookie-banner"
        >
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-2xl bg-[#FF2768]/12 flex items-center justify-center flex-shrink-0">
              <Cookie size={20} className="text-[#FF2768]" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] font-semibold mb-2">
                — Rispetto della tua privacy
              </p>
              <h3 className="font-display text-2xl text-[#3D2140] leading-tight mb-2">
                Un piccolo consenso, con cura.
              </h3>
              <p className="text-sm text-[#3D2140]/75 leading-relaxed mb-4">
                Uso cookie tecnici (necessari) e — se vuoi — cookie di analisi anonimi per capire cosa ti serve davvero. Nessun tracciamento pubblicitario. Puoi cambiare idea quando vuoi dalle{" "}
                <Link to="/cookie-policy" className="text-[#FF2768] underline hover:no-underline">
                  preferenze cookie
                </Link>.
              </p>

              {showDetails && (
                <div className="text-xs text-[#3D2140]/65 leading-relaxed mb-4 space-y-2 bg-white rounded-xl p-3 border border-[#3D2140]/8">
                  <p><strong className="text-[#3D2140]">Cookie tecnici (sempre attivi):</strong> necessari per il funzionamento del sito (login admin, newsletter, form kit gratuito).</p>
                  <p><strong className="text-[#3D2140]">Cookie di analisi (facoltativi):</strong> Google Analytics 4, dati anonimizzati per capire quali pagine sono più utili.</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={accept}
                  data-testid="cookie-accept"
                  className="bg-[#FF2768] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#E01F5A] transition-colors"
                >
                  Accetta tutti
                </button>
                <button
                  onClick={deny}
                  data-testid="cookie-deny"
                  className="bg-white border border-[#3D2140]/20 text-[#3D2140] rounded-full px-5 py-2.5 text-sm font-semibold hover:border-[#FF2768] hover:text-[#FF2768] transition-colors"
                >
                  Rifiuta
                </button>
                <button
                  onClick={() => setShowDetails((s) => !s)}
                  data-testid="cookie-details"
                  className="text-sm font-semibold text-[#3D2140]/70 hover:text-[#FF2768] transition-colors px-3"
                >
                  {showDetails ? "Nascondi dettagli" : "Personalizza"}
                </button>
              </div>
            </div>
            <button
              onClick={deny}
              aria-label="Chiudi (rifiuta)"
              className="h-8 w-8 rounded-full hover:bg-[#3D2140]/6 flex items-center justify-center flex-shrink-0"
            >
              <X size={16} className="text-[#3D2140]/60" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
